import React from 'react';
import { StyleSheet, css } from 'aphrodite';
import { FlatButton, RaisedButton } from 'material-ui';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';
import { gql, graphql } from 'react-apollo';

import * as InquiryActions from '../../store/actions/InquiryActions';
import AddTraveler from './AddTraveler';
import UpcomingTrips from './UpcomingTrips';
import SuggestedTravelers from './SuggestedTravelers';
import TravelerListItem from './TravelerListItem';
import TripDetails from './TripDetails';
import AddSegment from './AddSegment';
import SegmentListItem from './SegmentListItem';

const submitInquiry = gql`
  mutation inquiryFormSubmit($input: SubmitInquiryInput!) {
    submitInquiry(input: $input) {
      trip {
        id
      }
    }
  }
`;

@connect(state => ({
  inquiry: state.Inquiry.get('inquiry').toJS(),
  meta: state.Inquiry.get('meta').toJS(),
}))
@graphql(submitInquiry)
class InquiryForm extends React.Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    inquiry: PropTypes.shape().isRequired,
    meta: PropTypes.shape().isRequired,
    history: PropTypes.shape().isRequired,
    mutate: PropTypes.func.isRequired,
  }

  addTravelerBlock = () => {
    const { meta: { addingTraveler }, inquiry: { travelers } } = this.props;

    if (addingTraveler) {
      return <AddTraveler />;
    }

    if (travelers.length > 0) {
      const travelerIds = travelers.map(traveler => traveler.id).filter(id => id !== undefined && id !== '');

      return travelerIds.length > 0 ? (<div className={css(styles.suggestions)}>
        <SuggestedTravelers travelers={travelerIds} />
        <UpcomingTrips travelers={travelerIds} />
      </div>) : null;
    }

    return null;
  }

  toggleBoolean = name => () => {
    const { meta, dispatch } = this.props;
    const currentValue = meta[name];

    dispatch(InquiryActions.updateField(['meta', name], !currentValue));
  }

  cancel = () => {
    this.props.dispatch(InquiryActions.resetInquiryForm());
    this.props.history.goBack();
  }

  reset = () => {
    this.props.dispatch(InquiryActions.resetInquiryForm());
  }

  submit = () => {
    const { inquiry, mutate } = this.props;
    const { tripDetails: { travelStartDate, travelEndDate } } = inquiry;

    if (moment.isMoment(inquiry.tripDetails.travelStartDate)) {
      inquiry.tripDetails.travelStartDate = travelStartDate.format('YYYY-MM-DDTHH:mm:ssZZ');
    }
    if (moment.isMoment(inquiry.tripDetails.travelEndDate)) {
      inquiry.tripDetails.travelEndDate = travelEndDate.format('YYYY-MM-DDTHH:mm:ssZZ');
    }
    inquiry.travelers = inquiry.travelers.map((traveler) => {
      if (moment.isMoment(traveler.birthdate)) {
        traveler.birthdate = traveler.birthdate.format('YYYY-MM-DDTHH:mm:ssZZ');
      }
      return traveler;
    });
    inquiry.itinerary = inquiry.itinerary.map((segment) => {
      if (moment.isMoment(segment.startDate)) {
        segment.startDate = segment.startDate.format('YYYY-MM-DDTHH:mm:ssZZ');
      }
      if (moment.isMoment(segment.endDate)) {
        segment.endDate = segment.endDate.format('YYYY-MM-DDTHH:mm:ssZZ');
      }
      return segment;
    });

    mutate({
      variables: {
        input: { inquiry },
      },
    }).then(({ data: { submitInquiry: { trip: { id } } } }) => {
      this.props.dispatch(InquiryActions.resetInquiryForm());
      this.props.history.push(`/trips/${id}/`);
    });
  }

  render() {
    const {
      inquiry: { travelers, itinerary, tripDetails },
      meta: { addingTraveler, showOverview, addingSegment },
    } = this.props;

    const hasTravelers = travelers.length > 0;
    const inboxSet = tripDetails.inbox !== '';
    const userSet = tripDetails.selectedUser !== '';
    const categorySet = tripDetails.category !== '';
    const destinationSet = tripDetails.destination !== '';
    const datesSet = (
      tripDetails.specificDates && tripDetails.travelStartDate &&
      tripDetails.travelEndDate
    ) || (!tripDetails.specificDates && tripDetails.timeframe);
    const ready = (
      hasTravelers && inboxSet && userSet && categorySet && destinationSet &&
      datesSet
    );

    return (<div className={css(styles.container)}>
      <h1 className={css(styles.title)}>New Travel Inquiry</h1>
      <div className={css(styles.section)}>
        <h2>Travelers ({travelers.length})</h2>
        <div className={css(styles.listContainer)}>
          <div className={css(styles.itemList)}>
            {!hasTravelers ? <span className={css(styles.errorMessage)}>
              At least one traveler must be entered.
            </span> : null}
            <ol>
              {travelers.map((traveler, index) => (
                <TravelerListItem key={index} index={index} traveler={traveler} />
              ))}
            </ol>
            <FlatButton label='Add Traveler' disabled={addingTraveler} onTouchTap={this.toggleBoolean('addingTraveler')} />
          </div>
          {this.addTravelerBlock()}
        </div>
      </div>
      <div className={css(styles.section)}>
        <h2>
          Trip Overview <FlatButton
            label={showOverview ? 'hide' : 'show'}
            onTouchTap={this.toggleBoolean('showOverview')}
          />
        </h2>
        <div className={css(styles.listContainer)}>
          <div className={css(styles.itemList)}>
            <ul>
              {!inboxSet ? <li className={css(styles.errorMessage)}>
                An inbox must be selected.
              </li> : null}
              {!userSet ? <li className={css(styles.errorMessage)}>
                The trip must be assigned to someone.
              </li> : null}
              {!categorySet ? <li className={css(styles.errorMessage)}>
                A trip category must be selected.
              </li> : null}
              {!destinationSet ? <li className={css(styles.errorMessage)}>
                A destination must be entered.
              </li> : null}
              {!datesSet ? <li className={css(styles.errorMessage)}>
                Travel dates or a timeframe must be entered.
              </li> : null}
            </ul>
          </div>
          {showOverview ? <TripDetails /> : null}
        </div>
      </div>
      <div className={css(styles.section)}>
        <h2>Segments ({itinerary.length})</h2>
        <div className={css(styles.listContainer)}>
          <div className={css(styles.itemList)}>
            <ol>
              {itinerary.map((segment, index) => <SegmentListItem
                key={index}
                index={index}
                segment={segment}
              />)}
            </ol>
            <FlatButton label='Add Segment' disabled={addingSegment} onTouchTap={this.toggleBoolean('addingSegment')} />
          </div>
          {addingSegment ? <AddSegment /> : null}
        </div>
      </div>
      <div className={css(styles.formActions)}>
        <RaisedButton className={css(styles.actionButton)} label='Reset' secondary onTouchTap={this.reset} />
        <RaisedButton className={css(styles.actionButton)} label='Cancel' secondary onTouchTap={this.cancel} />
        <RaisedButton
          className={css(styles.actionButton)}
          label='Submit Inquiry'
          primary
          onTouchTap={this.submit}
          disabled={!ready}
        />
      </div>
    </div>);
  }
}

const styles = StyleSheet.create({
  container: {
    padding: 15,
  },
  title: {
    margin: 0,
  },
  section: {
    marginTop: 15,
  },
  listContainer: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  itemList: {
    fontSize: 13,
    marginLeft: 15,
    marginTop: 5,
    width: 300,
  },
  suggestions: {
    display: 'flex',
    justifyContent: 'space-around',
  },
  formActions: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 15,
  },
  actionButton: {
    marginLeft: 15,
  },
  errorMessage: {
    color: 'red',
    fontSize: 13,
  },
});

export default InquiryForm;



// WEBPACK FOOTER //
// /src/components/Inquiry/InquiryForm.js